/**
 * «Подтверждение семьи» for the asked member (docs/cabinet-redesign/04 §0.2 З-7). Customer-JWT zone —
 * /api/v1/showcase/me/identity-vouches, answered by ShowcaseIdentityVouchController (IdentityVouchService). Support asks
 * members of a family office to vouch for a caller who lost access to the app; each asked member answers once, finally,
 * from the card in their chat: «Подтверждаю» (a step-up of purpose IDENTITY_VOUCH) or «Это не он».
 * A refusal is `{"error": CODE}` (translated under vitrine.error.*).
 */
import { http } from './http';

const BASE = '/v1/showcase/me/identity-vouches';

/**
 * Where the request is (IdentityVouchViews.State): PENDING — waiting for answers; PASSED — the family vouched; FAILED —
 * someone answered «Это не он»; EXPIRED — the time ran out; CANCELLED — support asked again, this one is closed.
 */
export type IdentityVouchState = 'PENDING' | 'PASSED' | 'FAILED' | 'EXPIRED' | 'CANCELLED';

export type IdentityVouchAnswer = 'CONFIRMED' | 'DECLINED';

/** IdentityVouchViews.MemberView — the request as the asked member sees it. */
export interface IdentityVouchView {
  id: string;
  state: IdentityVouchState;
  /** Who asks to be vouched for — the family member's name. */
  subjectName: string | null;
  /** My answer; null while I have not answered. */
  answer: IdentityVouchAnswer | null;
  answeredAt: string | null;
  /** PENDING and not answered yet — the buttons are shown. */
  canAnswer: boolean;
  createdAt: string;
  expiresAt: string;
}

export const identityVouchApi = {
  async one(id: string): Promise<IdentityVouchView> {
    return (await http.get<IdentityVouchView>(`${BASE}/${encodeURIComponent(id)}`)).data;
  },

  /**
   * «Подтверждаю». Without `grant` the server answers 403 STEP_UP_REQUIRED with the factors once every other check
   * passed; call again with the grant of purpose IDENTITY_VOUCH.
   */
  async confirm(id: string, grant?: string): Promise<IdentityVouchView> {
    const headers: Record<string, string> = grant ? { 'X-Reveal-Grant': grant } : {};
    return (await http.post<IdentityVouchView>(`${BASE}/${encodeURIComponent(id)}/confirm`, null, { headers })).data;
  },

  /** «Это не он» — final; the request fails at once. */
  async decline(id: string): Promise<IdentityVouchView> {
    return (await http.post<IdentityVouchView>(`${BASE}/${encodeURIComponent(id)}/decline`)).data;
  },
};
